import React, { JSX, useMemo, useRef, useState } from 'react'
import { EmoteWallMockup } from 'components/overlayPreview/OverlayMockups'
import { usePublicHomePreviewSeam } from 'components/overlayPreview/usePreviewSeam'
import { usePrefersReducedMotion } from 'components/overlayPreview/previewFeeds'
import { PUBLIC_HOME_COPY as COPY } from 'lib/publicHomeCopy'
import { useElementWidth, useNearViewport, wallDesignSize } from 'components/publicHome/publicHomeHooks'
import { HOME_FRAME_CLASSES, LEGEND_CLASSES, RadioPill } from 'components/publicHome/ChatOverlayDemo'
import { DEMO_SEED, DEMO_WALL_CONFIG, DEMO_WALL_STYLES, DemoWallStyle } from 'components/publicHome/publicHomeSamples'

const STYLE_LABELS: Readonly<Record<DemoWallStyle, string>> = {
  RAIN: COPY.emotes.rain,
  POP: COPY.emotes.pop
}

const CAPTION_CLASSES = 'mt-3 text-xs leading-[18px] text-dm-text-accent sm:text-[13px] sm:leading-5'

export const EmoteWallDemo = (): JSX.Element => {
  const seam = usePublicHomePreviewSeam(DEMO_SEED)
  const sceneRef = useRef<HTMLDivElement | null>(null)
  const near = useNearViewport(sceneRef)
  const frameWidth = useElementWidth(sceneRef)
  const design = wallDesignSize(frameWidth)
  const reducedMotion = usePrefersReducedMotion(true)
  const [style, setStyle] = useState<DemoWallStyle>('RAIN')
  const config = useMemo(() => ({ ...DEMO_WALL_CONFIG, style }), [style])

  return (
    <figure className='w-full max-w-full'>
      <fieldset className='mb-3'>
        <legend className={LEGEND_CLASSES}>{COPY.emotes.styleLabel}</legend>
        <div className='mt-2 inline-flex gap-1 rounded-full p-1'>
          {DEMO_WALL_STYLES.map((value) => (
            <RadioPill
              key={value}
              name='public-home-demo-wall-style'
              value={value}
              checked={style === value}
              onSelect={setStyle}
            >
              {STYLE_LABELS[value]}
            </RadioPill>
          ))}
        </div>
      </fieldset>
      <div className={HOME_FRAME_CLASSES}>
        <div
          ref={sceneRef}
          aria-hidden='true'
          className='relative aspect-video w-full overflow-hidden bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-600'
        >
          {near && frameWidth > 0 && (
            <div className='absolute inset-0'>
              <EmoteWallMockup
                seam={seam}
                config={config}
                designWidth={design.width}
                designHeight={design.height}
              />
            </div>
          )}
        </div>
      </div>
      <figcaption className={CAPTION_CLASSES}>{COPY.emotes.demoCaption}</figcaption>
      {reducedMotion && <p className={CAPTION_CLASSES}>{COPY.reducedMotionWall}</p>}
    </figure>
  )
}
